import { Link } from 'react-router-dom';
import {
  OUTREACH_CHANNEL_LABEL,
  OUTREACH_OUTCOME_LABEL,
  OUTREACH_STATUS_LABEL,
  type SalesOutreach,
} from '@/client/types/sales';
import { cn } from '@/lib/utils';
import { useSalesRepsQuery } from '@/client/api/sales/sales';
import { initials } from './sales-ui';

export function threadItems(rows: SalesOutreach[]): SalesOutreach[] {
  return rows
    .filter((o) => o.status !== 'blocked')
    .slice()
    .sort((a, b) => a.at.localeCompare(b.at));
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function ConversationThread({
  items,
  prospectName,
  empty = 'No messages yet.',
}: {
  items: SalesOutreach[];
  prospectName: string;
  empty?: string;
}) {
  const reps = useSalesRepsQuery().data ?? [];
  const getRep = (id: string) => reps.find((r) => r.id === id);
  const rows = threadItems(items);
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400">{empty}</p>;
  }

  return (
    <ol className="space-y-3">
      {rows.map((o) => {
        const inbound = o.direction === 'inbound';
        const who = inbound ? prospectName : (getRep(o.actorId)?.name ?? 'Sales');
        // An outbound touch is logged on the floor, not necessarily delivered:
        // off-platform (SMS/email/phone) is never sent for you — hence the
        // explicit "not delivered". on-platform is recorded as a platform touch.
        const outboundTouch = !inbound && (o.status === 'sent' || o.status === 'logged');
        const statusLabel = outboundTouch
          ? (o.mode === 'off_platform' ? 'Logged · not delivered' : 'Recorded on-platform')
          : OUTREACH_STATUS_LABEL[o.status];
        return (
          <li key={o.id} className={cn('flex gap-2', inbound ? 'justify-start' : 'justify-end')}>
            {inbound ? (
              <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-800 text-[10px] font-semibold text-white">
                {initials(who)}
              </span>
            ) : null}
            <div className={cn('max-w-[min(36rem,85%)]', inbound ? '' : 'text-right')}>
              <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-400">
                <span className="font-medium text-gray-500">{who}</span>
                <span>{OUTREACH_CHANNEL_LABEL[o.channel]}</span>
                {outboundTouch || (o.status !== 'logged' && o.status !== 'replied') ? (
                  <span>{statusLabel}</span>
                ) : null}
                <span>{when(o.at)}</span>
              </div>
              <div
                className={cn(
                  'rounded-2xl px-3.5 py-2.5 text-left text-sm leading-snug',
                  inbound ? 'rounded-tl-md bg-gray-100 text-gray-900' : 'rounded-tr-md bg-slate-900 text-white',
                  o.status === 'draft' && 'border border-dashed border-gray-300 bg-white text-gray-700',
                )}
              >
                {o.body}
              </div>
              {o.trackedLinks.length > 0 ? (
                <div className="mt-1.5 flex flex-wrap justify-end gap-2 text-[11px]">
                  {o.trackedLinks.map((l) => (
                    <Link key={l.token} className="text-blue-700 hover:underline" to={`/admin/sales/r/${l.token}`}>
                      {l.dest}
                      {l.events.length ? ` · ${l.events.length}` : ''}
                    </Link>
                  ))}
                </div>
              ) : null}
              {o.feedback ? (
                <p className="mt-1 text-[11px] text-gray-400">
                  {o.feedback.sentiment} · {OUTREACH_OUTCOME_LABEL[o.feedback.outcome]}
                  {o.feedback.note ? ` · ${o.feedback.note}` : ''}
                </p>
              ) : null}
            </div>
            {!inbound ? (
              <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700">
                {initials(who)}
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
